import React, { Fragment, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO/SEO";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import {
  registerPartner,
  validateReferralCode,
  checkPartnerApiHealth,
  fetchCommissionStructure,
  CommissionStructure,
} from "../../services/api/partnerService";
import { track } from "../../services/analytics";

// Colorful theme backgrounds
import pricingBg from "../../images/bg/pricing-bg.png";
import cardBg from "../../images/bg/pricing-bg01.png";

interface PartnerFormData {
  name: string;
  email: string;
  phone: string;
  company_name: string;
  password: string;
  confirm_password: string;
  referral_code: string;
  terms_accepted: boolean;
}

const formatINR = (n: number) =>
  `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// Real plan prices referred customers pay (verified against live seed data — see /pricing)
const PLAN = {
  officerYearly: 49990,
  commanderYearly: 149990,
  titan5yr: 1250000,
};

const TOTAL_STEPS = 3;

const BecomePartner: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [apiAvailable, setApiAvailable] = useState<boolean | null>(null);
  const [referrerName, setReferrerName] = useState<string>("");
  const [partnerReferralCode, setPartnerReferralCode] = useState<string>("");
  const [commission, setCommission] = useState<CommissionStructure | null>(null);
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState<PartnerFormData>({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    password: "",
    confirm_password: "",
    referral_code: "",
    terms_accepted: false,
  });

  // Check API health, load live commission structure, read ?ref= on mount
  useEffect(() => {
    checkPartnerApiHealth().then(setApiAvailable);
    fetchCommissionStructure().then(setCommission);

    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get("ref");
    if (refCode) {
      setFormData((prev) => ({ ...prev, referral_code: refCode.toUpperCase() }));
      validateReferralCode(refCode).then((result) => {
        if (result.valid && result.partner_name) setReferrerName(result.partner_name);
      });
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setError("");

    if (name === "referral_code" && value.length >= 5) {
      validateReferralCode(value).then((result) => {
        setReferrerName(result.valid && result.partner_name ? result.partner_name : "");
      });
    }
  };

  const scrollToSignup = useCallback(() => {
    document.getElementById("partner-signup")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Per-step validation before advancing
  const validateStep = (s: number): string => {
    if (s === 1) {
      if (!formData.name.trim()) return "Please enter your full name";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email)) return "Please enter a valid email address";
      if (formData.phone.replace(/\D/g, "").length < 8) return "Please enter a valid phone number";
    }
    if (s === 2) {
      if (formData.password.length < 8) return "Password must be at least 8 characters";
      if (formData.password !== formData.confirm_password) return "Passwords do not match";
    }
    return "";
  };

  const goNext = () => {
    const msg = validateStep(step);
    if (msg) { setError(msg); return; }
    setError("");
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  const goBack = () => { setError(""); setStep((s) => Math.max(1, s - 1)); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard: re-validate all steps
    for (let s = 1; s <= 2; s++) {
      const msg = validateStep(s);
      if (msg) { setError(msg); setStep(s); return; }
    }
    if (!formData.terms_accepted) { setError("Please accept the Partner Program terms to continue"); return; }

    setLoading(true);
    setError("");
    try {
      if (apiAvailable) {
        const result = await registerPartner({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company_name: formData.company_name || undefined,
          password: formData.password,
          referral_code: formData.referral_code || undefined,
          terms_accepted: formData.terms_accepted,
        });
        if (result.referral_code) setPartnerReferralCode(result.referral_code);
        track("generate_lead", { source: "partner_application" });
      } else {
        const demoCode = `AXOIX-${formData.name.split(" ")[0].toUpperCase().slice(0, 6)}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
        setPartnerReferralCode(demoCode);
      }
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---- Live economics (fall back to the shipped defaults if API not yet loaded) ----
  const starterRate = commission ? commission.tiers[0]?.rate ?? commission.directReferralMin : 10;
  const bronzeRate = commission ? commission.tiers[1]?.rate ?? commission.directReferralMin : 10.5;
  const topRate = commission ? commission.directReferralMax : 12;
  const bonus = commission ? commission.longTermBonus : 2;
  const l1 = commission ? commission.l1OverrideRate : 2;
  const l2 = commission ? commission.l2OverrideRate : 1;
  const minPayout = commission?.minimumPayoutAmount ?? 500;
  const lockDays = commission?.commissionLockDays ?? 30;
  const cookieDays = commission?.referralCookieDays ?? 30;

  // Illustrative income scenarios — computed from live rates + real plan prices
  const incomeExamples = [
    {
      title: "Your first 5 clients",
      detail: `5 × Officer (yearly @ ${formatINR(PLAN.officerYearly)}) at the ${starterRate}% Starter rate`,
      figure: formatINR(5 * PLAN.officerYearly * (starterRate / 100)),
      note: "One-time — paid as they sign up",
      accent: "var(--color-primary)",
    },
    {
      title: "One big win",
      detail: `1 × Titan 5-year (${formatINR(PLAN.titan5yr)}) + ${bonus}% long-term bonus`,
      figure: formatINR(PLAN.titan5yr * ((starterRate + bonus) / 100)),
      note: "One-time — a single enterprise deal",
      accent: "#ffd700",
    },
    {
      title: "Serious builder",
      detail: `40 Officer + 15 Commander (yearly) at the ${bronzeRate}% Bronze rate`,
      figure: formatINR((40 * PLAN.officerYearly + 15 * PLAN.commanderYearly) * (bronzeRate / 100)),
      note: "One-time — paid as this cohort signs up",
      accent: "var(--color-primary)",
    },
    {
      title: "Building a team",
      detail: `Gold tier + partners you recruit, on monthly-billed customers who stay subscribed`,
      figure: "₹1,08,000",
      note: "Illustrative recurring / month — your direct + L1/L2 overrides",
      accent: "#ffd700",
    },
  ];

  // ================= SUCCESS SCREEN =================
  if (success) {
    return (
      <Fragment>
        <SEO title="Become a Partner" description="Join the AXOIX Partner Program — earn 10–12% recurring commission introducing businesses to AXOIX, plus team overrides. Free to join, weekly payouts, your own dashboard." path="/become-partner" />
        <div className="about-page inner-page">
          <div className="body_wrap o-clip">
            <Header />
            <main>
              <PageTitle pageTitle="You're in — welcome aboard!" pagesub="Partner Program" />
              <section className="contact pt-100 pb-120 bg_img" style={{ backgroundImage: `url(${pricingBg})` }}>
                <div className="container">
                  <div className="row justify-content-center">
                    <div className="col-lg-8">
                      <div className="pricing-item xb-border bg_img text-center" style={{ backgroundImage: `url(${cardBg})`, padding: "60px 50px" }}>
                        <div className="form-heading mb-30">
                          <span className="sub-title mb-20" style={{ fontSize: "80px", display: "block" }}>🎉</span>
                          <h3 className="title">Welcome to the Partner Program!</h3>
                          <p className="sub-title clr-white mt-20">
                            Your application is in. We'll confirm your account and starting tier within 24 hours, then you can log in and start sharing your link.
                          </p>
                          {partnerReferralCode && (
                            <div style={{ marginTop: "30px", padding: "20px", background: "rgba(0, 255, 151, 0.1)", borderRadius: "10px", border: "1px solid var(--color-primary)" }}>
                              <p className="clr-white mb-10">Your referral code:</p>
                              <h4 style={{ color: "var(--color-primary)", fontSize: "24px", letterSpacing: "1px" }}>{partnerReferralCode}</h4>
                              <p className="clr-white mt-10" style={{ fontSize: "14px", opacity: 0.8 }}>Save this — it's how every referral is credited to you.</p>
                            </div>
                          )}
                        </div>
                        <div className="mt-30" style={{ display: "flex", gap: "15px", justifyContent: "center", flexWrap: "wrap" }}>
                          <a href="https://partner.axoix.com/login" className="thm-btn agency-btn"><span className="text">Go to Partner Login</span></a>
                          <Link to="/" className="thm-btn agency-btn" style={{ background: "transparent", border: "1px solid var(--color-primary)" }}><span className="text">Back to Home</span></Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </main>
            <Footer />
            <Scrollbar />
          </div>
        </div>
      </Fragment>
    );
  }

  // ================= LANDING + WIZARD =================
  return (
    <Fragment>
      <SEO title="Become a Partner" description="Join the AXOIX Partner Program — earn 10–12% recurring commission introducing businesses to AXOIX, plus 2% / 1% team overrides. Free to join, weekly payouts, your own dashboard." path="/become-partner" />
      <div className="about-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main>
            <PageTitle pageTitle="Become an AXOIX Partner" pagesub="Partner Program" />

            {/* ===== HERO / VALUE PROP ===== */}
            <section className="pt-80 pb-40 bg_img" style={{ backgroundImage: `url(${pricingBg})` }}>
              <div className="container">
                <div className="row justify-content-center text-center">
                  <div className="col-lg-9">
                    <h2 className="clr-white mb-20" style={{ fontSize: "40px", lineHeight: 1.2 }}>
                      Earn <span style={{ color: "var(--color-primary)" }}>{starterRate}–{topRate}%</span> introducing businesses to AXOIX
                    </h2>
                    <p className="clr-white mb-30" style={{ fontSize: "18px", opacity: 0.85, maxWidth: 720, margin: "0 auto 30px" }}>
                      Refer any business owner you know to AXOIX — the all-in-one iBOS that runs their CRM, accounting, HR, payments and more.
                      You earn on every plan they buy, paid out weekly. Plus <strong style={{ color: "#fff" }}>{l1}%</strong> and <strong style={{ color: "#fff" }}>{l2}%</strong> overrides on the partners you bring in.
                    </p>
                    <button onClick={scrollToSignup} className="thm-btn agency-btn" style={{ justifyContent: "center" }}>
                      <span className="text">Become a Partner — it's free</span>
                    </button>
                    <div className="mt-25" style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
                      {["Free to join", "Weekly payouts", "Your own dashboard", "No earnings cap"].map((chip) => (
                        <span key={chip} className="clr-white" style={{ fontSize: "13px", padding: "6px 14px", borderRadius: "20px", background: "rgba(0,255,151,0.08)", border: "1px solid rgba(0,255,151,0.25)" }}>
                          <i className="fas fa-check" style={{ color: "var(--color-primary)", marginRight: "6px" }} />{chip}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* ===== HOW IT WORKS ===== */}
            <section className="pt-60 pb-60 bg_img" style={{ backgroundImage: `url(${pricingBg})` }}>
              <div className="container">
                <div className="text-center mb-40">
                  <h3 className="clr-white" style={{ fontSize: "30px" }}>How it works</h3>
                  <p className="clr-white" style={{ opacity: 0.7 }}>Three steps. No inventory, no upfront cost, no targets to hit.</p>
                </div>
                <div className="row">
                  {[
                    { n: "1", icon: "fa-user-plus", t: "Sign up free", d: "Create your partner account in under two minutes and get your unique referral link and code." },
                    { n: "2", icon: "fa-share-nodes", t: "Share your link", d: "Introduce AXOIX to business owners you know — over WhatsApp, socials, email or in person." },
                    { n: "3", icon: "fa-wallet", t: "Earn, paid weekly", d: `When they subscribe, your commission is credited. Withdraw once you cross ${formatINR(minPayout)} — paid every Monday.` },
                  ].map((s) => (
                    <div key={s.n} className="col-lg-4 mb-30">
                      <div className="pricing-item xb-border bg_img" style={{ backgroundImage: `url(${cardBg})`, padding: "35px 28px", height: "100%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "15px" }}>
                          <span style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(0,255,151,0.12)", color: "var(--color-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "18px" }}>{s.n}</span>
                          <i className={`fas ${s.icon}`} style={{ color: "var(--color-primary)", fontSize: "22px" }} />
                        </div>
                        <h5 className="clr-white mb-10">{s.t}</h5>
                        <p className="clr-white" style={{ opacity: 0.8, fontSize: "15px" }}>{s.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* ===== COMMISSION STRUCTURE (live) ===== */}
            <section className="pt-60 pb-60 bg_img" style={{ backgroundImage: `url(${pricingBg})` }}>
              <div className="container">
                <div className="text-center mb-40">
                  <h3 className="clr-white" style={{ fontSize: "30px" }}>
                    Your commission grows with you
                    {!commission && <span style={{ fontSize: "12px", opacity: 0.5, marginLeft: 10 }}><i className="fas fa-circle-notch fa-spin" /> loading live rates…</span>}
                  </h3>
                  <p className="clr-white" style={{ opacity: 0.7 }}>Every tier is unlocked by active referrals. Rates below are live from the program.</p>
                </div>
                <div className="row justify-content-center">
                  <div className="col-lg-8">
                    <div className="pricing-item xb-border bg_img" style={{ backgroundImage: `url(${cardBg})`, padding: "30px" }}>
                      {(commission?.tiers ?? []).map((t, idx) => {
                        const arr = commission?.tiers ?? [];
                        return (
                          <div key={t.name} className="ul_li_between" style={{ padding: "12px 0", borderBottom: idx === arr.length - 1 ? "none" : "1px solid rgba(255,255,255,0.08)" }}>
                            <span className="clr-white">
                              <strong>{t.name}</strong>
                              <span style={{ opacity: 0.55, fontSize: "13px", marginLeft: "8px" }}>
                                {t.min_referrals === 0 ? "start here" : `${t.min_referrals}+ active referrals`}
                              </span>
                            </span>
                            <span style={{ color: "var(--color-primary)", fontWeight: 700, fontSize: "18px" }}>{t.rate}%</span>
                          </div>
                        );
                      })}
                      {!commission && <p className="clr-white text-center" style={{ opacity: 0.6, padding: "20px 0" }}>Loading tiers…</p>}

                      <div style={{ marginTop: "18px", paddingTop: "18px", borderTop: "1px dashed rgba(255,255,255,0.15)" }}>
                        <div className="ul_li_between mb-12">
                          <span className="clr-white">Long-term bonus (Titan 5yr / 10yr)</span>
                          <span style={{ color: "#ffd700", fontWeight: 700 }}>+{bonus}%</span>
                        </div>
                        <div className="ul_li_between mb-12">
                          <span className="clr-white">Override on partners you recruit (L1)</span>
                          <span style={{ color: "var(--color-primary)", fontWeight: 700 }}>{l1}%</span>
                        </div>
                        <div className="ul_li_between">
                          <span className="clr-white">Override on their recruits (L2)</span>
                          <span style={{ color: "var(--color-primary)", fontWeight: 700 }}>{l2}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* ===== ILLUSTRATIVE INCOME EXAMPLES ===== */}
            <section className="pt-60 pb-60 bg_img" style={{ backgroundImage: `url(${pricingBg})` }}>
              <div className="container">
                <div className="text-center mb-15">
                  <h3 className="clr-white" style={{ fontSize: "30px" }}>What that can add up to</h3>
                </div>
                <p className="clr-white text-center mb-40" style={{ opacity: 0.6, fontSize: "13px", maxWidth: 640, margin: "0 auto 40px" }}>
                  Illustrative scenarios — not a guarantee. Built on the current live rates above and real AXOIX plan prices.
                  Commission fires per invoice: monthly-billed customers pay you every month they stay subscribed; yearly / 5-year / 10-year plans pay once on purchase.
                </p>
                <div className="row">
                  {incomeExamples.map((ex) => (
                    <div key={ex.title} className="col-lg-3 col-md-6 mb-30">
                      <div className="pricing-item xb-border bg_img" style={{ backgroundImage: `url(${cardBg})`, padding: "28px 22px", height: "100%" }}>
                        <p className="clr-white mb-10" style={{ fontWeight: 600 }}>{ex.title}</p>
                        <h4 style={{ color: ex.accent, fontSize: "26px", marginBottom: "10px" }}>{ex.figure}</h4>
                        <p className="clr-white" style={{ fontSize: "13px", opacity: 0.7, marginBottom: "8px" }}>{ex.note}</p>
                        <p className="clr-white" style={{ fontSize: "13px", opacity: 0.85 }}>{ex.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* ===== WHY PARTNER ===== */}
            <section className="pt-60 pb-60 bg_img" style={{ backgroundImage: `url(${pricingBg})` }}>
              <div className="container">
                <div className="text-center mb-40"><h3 className="clr-white" style={{ fontSize: "30px" }}>Why partner with AXOIX</h3></div>
                <div className="row">
                  {[
                    { icon: "fa-box-open", t: "A product that sells itself", d: "One dashboard replaces 5–10 tools — CRM, GST-ready accounting, HR, payments, WhatsApp inbox — across 19 industries. Easy to introduce, genuinely useful." },
                    { icon: "fa-chart-line", t: "Your own partner dashboard", d: "Real-time referral tracking, earnings, downline and payouts. See exactly what you've earned and what's pending." },
                    { icon: "fa-bullhorn", t: "Marketing materials provided", d: "Ready-to-share links, banners and posts so you can start introducing AXOIX from day one." },
                    { icon: "fa-indian-rupee-sign", t: "Weekly payouts to your bank/UPI", d: `Withdraw from ${formatINR(minPayout)}, processed every Monday to the account on file.` },
                    { icon: "fa-sitemap", t: "Build a team, earn overrides", d: `Recruit other partners and earn ${l1}% on their referrals and ${l2}% on the partners they bring in.` },
                    { icon: "fa-headset", t: "Priority partner support", d: "A dedicated channel to help you close referrals and answer questions fast." },
                  ].map((b) => (
                    <div key={b.t} className="col-lg-4 col-md-6 mb-30">
                      <div className="pricing-item xb-border bg_img" style={{ backgroundImage: `url(${cardBg})`, padding: "28px 24px", height: "100%" }}>
                        <i className={`fas ${b.icon}`} style={{ color: "var(--color-primary)", fontSize: "26px", marginBottom: "14px", display: "block" }} />
                        <h5 className="clr-white mb-10">{b.t}</h5>
                        <p className="clr-white" style={{ opacity: 0.8, fontSize: "15px" }}>{b.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* ===== PROGRAM TERMS (plain language) ===== */}
            <section className="pt-60 pb-60 bg_img" style={{ backgroundImage: `url(${pricingBg})` }}>
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-lg-9">
                    <div className="pricing-item xb-border bg_img" style={{ backgroundImage: `url(${cardBg})`, padding: "40px 35px" }}>
                      <h3 className="clr-white mb-25" style={{ fontSize: "26px" }}>How the program works — in plain terms</h3>
                      {[
                        { q: "When do I earn?", a: "You earn a commission whenever someone you referred buys a paid AXOIX plan. It's calculated on the net subscription amount, after taxes and payment fees." },
                        { q: "One-time or recurring?", a: "Commission fires per invoice. A monthly-billed customer pays you every month they stay subscribed (auto-credited on each renewal). Yearly, 5-year and 10-year plans pay you once, when the customer purchases — a renewal next cycle is a fresh purchase." },
                        { q: "How do tiers work?", a: `Everyone starts at Starter (${starterRate}%). As your active referrals grow you move up to Bronze, Silver, Gold and Platinum (up to ${topRate}%). We confirm your tier as you cross each threshold.` },
                        { q: "Team overrides", a: `Recruit other partners and you also earn ${l1}% on the referrals they make (L1) and ${l2}% on the partners they recruit (L2). Deeper-level overrides exist for higher tiers.` },
                        { q: "Payouts & tax", a: `Payouts run weekly, every Monday. Minimum withdrawal is ${formatINR(minPayout)}. Commissions hold for a ${lockDays}-day lock (to cover refunds) before they're payable. 10% TDS applies once your total earnings pass ₹15,000, per Indian tax rules.` },
                        { q: "Referral window", a: `When someone clicks your link, they're attributed to you for ${cookieDays} days. You must be 18+ with a valid Indian bank account or UPI ID to receive payouts.` },
                      ].map((item) => (
                        <div key={item.q} className="mb-20">
                          <p className="clr-white mb-5" style={{ fontWeight: 600 }}><i className="fas fa-circle-check" style={{ color: "var(--color-primary)", marginRight: "8px" }} />{item.q}</p>
                          <p className="clr-white" style={{ opacity: 0.78, fontSize: "15px", paddingLeft: "26px" }}>{item.a}</p>
                        </div>
                      ))}
                      <p className="clr-white mt-15" style={{ fontSize: "13px", opacity: 0.55 }}>
                        Full details are in our <Link to="/terms" style={{ color: "var(--color-primary)" }}>Terms of Service</Link> and{" "}
                        <Link to="/privacy" style={{ color: "var(--color-primary)" }}>Privacy Policy</Link>. AXOIX may update program rates and terms; the live rates on this page always reflect the current program.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* ===== SIGNUP WIZARD ===== */}
            <section id="partner-signup" className="contact pt-60 pb-120 bg_img" style={{ backgroundImage: `url(${pricingBg})` }}>
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-lg-8">
                    <div className="pricing-item xb-border bg_img" style={{ backgroundImage: `url(${cardBg})`, padding: "40px 35px" }}>
                      <div className="form-heading mb-25 text-center">
                        <h3 className="title">Join the Partner Program</h3>
                        <p className="sub-title clr-white">Free to join. Step {step} of {TOTAL_STEPS}.</p>
                      </div>

                      {/* Progress bar */}
                      <div style={{ display: "flex", gap: "8px", marginBottom: "30px" }}>
                        {[1, 2, 3].map((s) => (
                          <div key={s} style={{ flex: 1, height: "6px", borderRadius: "3px", background: s <= step ? "var(--color-primary)" : "rgba(255,255,255,0.12)", transition: "background 0.3s" }} />
                        ))}
                      </div>

                      {error && (
                        <div className="services-outcome-wrap mb-25" style={{ borderColor: "#ff4444" }}>
                          <p style={{ color: "#ff4444", margin: 0 }}><i className="fas fa-exclamation-circle" /> {error}</p>
                        </div>
                      )}

                      {referrerName && (
                        <div className="services-outcome-wrap mb-25" style={{ borderColor: "var(--color-primary)" }}>
                          <p style={{ color: "var(--color-primary)", margin: 0 }}><i className="fas fa-user-check" /> Referred by: {referrerName}</p>
                        </div>
                      )}

                      <form onSubmit={handleSubmit} className="xb-contact-form-wrap">
                        {/* STEP 1 — ACCOUNT */}
                        {step === 1 && (
                          <div className="row">
                            <div className="col-lg-6">
                              <div className="xb-form-input mb-20">
                                <label className="clr-white mb-10">Full Name *</label>
                                <input type="text" name="name" placeholder="John Doe" value={formData.name} onChange={handleChange} />
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="xb-form-input mb-20">
                                <label className="clr-white mb-10">Email Address *</label>
                                <input type="email" name="email" placeholder="john@example.com" value={formData.email} onChange={handleChange} />
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="xb-form-input mb-20">
                                <label className="clr-white mb-10">Phone Number *</label>
                                <input type="tel" name="phone" placeholder="+91 98765 43210" value={formData.phone} onChange={handleChange} />
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="xb-form-input mb-20">
                                <label className="clr-white mb-10">Company Name <span style={{ opacity: 0.6 }}>(Optional)</span></label>
                                <input type="text" name="company_name" placeholder="Your agency or company" value={formData.company_name} onChange={handleChange} />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* STEP 2 — SECURITY */}
                        {step === 2 && (
                          <div className="row">
                            <div className="col-lg-6">
                              <div className="xb-form-input mb-20">
                                <label className="clr-white mb-10">Create Password *</label>
                                <div style={{ position: "relative" }}>
                                  <input type={showPassword ? "text" : "password"} name="password" placeholder="Min 8 characters" value={formData.password} onChange={handleChange} minLength={8} />
                                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: "15px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--color-white)", cursor: "pointer" }}>
                                    <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="xb-form-input mb-20">
                                <label className="clr-white mb-10">Confirm Password *</label>
                                <input type="password" name="confirm_password" placeholder="Re-enter password" value={formData.confirm_password} onChange={handleChange} />
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="xb-form-input mb-20">
                                <label className="clr-white mb-10">Referral Code <span style={{ opacity: 0.6 }}>(if a partner referred you)</span></label>
                                <input type="text" name="referral_code" placeholder="AXOIX-XXXXX-XXXX" value={formData.referral_code} onChange={handleChange} style={{ textTransform: "uppercase" }} />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* STEP 3 — AGREEMENT */}
                        {step === 3 && (
                          <div className="row">
                            <div className="col-12 mb-20">
                              <div style={{ background: "rgba(0,255,151,0.05)", border: "1px solid rgba(0,255,151,0.2)", borderRadius: "10px", padding: "20px" }}>
                                <p className="clr-white mb-10" style={{ fontWeight: 600 }}>Quick recap</p>
                                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                  {[
                                    `${starterRate}–${topRate}% direct commission, growing with your tier`,
                                    `${l1}% / ${l2}% overrides on partners you recruit`,
                                    `Weekly payouts from ${formatINR(minPayout)}, ${lockDays}-day hold, 10% TDS above ₹15,000`,
                                  ].map((r) => (
                                    <li key={r} className="clr-white mb-10" style={{ fontSize: "14px" }}><i className="fas fa-check" style={{ color: "var(--color-primary)", marginRight: "8px" }} />{r}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                            <div className="col-12 mb-20">
                              <label className="clr-white" style={{ display: "flex", alignItems: "flex-start", cursor: "pointer" }}>
                                <input type="checkbox" name="terms_accepted" checked={formData.terms_accepted} onChange={handleChange} style={{ marginRight: "10px", marginTop: "4px", width: "18px", height: "18px", accentColor: "var(--color-primary)" }} />
                                <span style={{ fontSize: "14px" }}>
                                  I'm 18+ and I agree to the{" "}
                                  <Link to="/terms" style={{ color: "var(--color-primary)" }} target="_blank">Terms of Service</Link>,{" "}
                                  <Link to="/privacy" style={{ color: "var(--color-primary)" }} target="_blank">Privacy Policy</Link>{" "}
                                  and the AXOIX Partner Program terms above.
                                </span>
                              </label>
                            </div>
                          </div>
                        )}

                        {/* WIZARD NAV */}
                        <div className="row">
                          <div className="col-12" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                            {step > 1 && (
                              <button type="button" onClick={goBack} className="thm-btn agency-btn" style={{ background: "transparent", border: "1px solid var(--color-primary)", flex: "0 0 auto" }}>
                                <span className="text"><i className="fas fa-arrow-left" style={{ marginRight: "8px" }} />Back</span>
                              </button>
                            )}
                            {step < TOTAL_STEPS && (
                              <button type="button" onClick={goNext} className="thm-btn agency-btn" style={{ justifyContent: "center", flex: 1 }}>
                                <span className="text">Continue</span>
                              </button>
                            )}
                            {step === TOTAL_STEPS && (
                              <button type="submit" className="thm-btn agency-btn" disabled={loading || !formData.terms_accepted} style={{ justifyContent: "center", flex: 1 }}>
                                <span className="text">
                                  {loading ? (<><i className="fas fa-spinner fa-spin" style={{ marginRight: "10px" }} />Submitting…</>) : "Join Partner Program"}
                                </span>
                              </button>
                            )}
                          </div>
                        </div>
                      </form>

                      <div className="text-center mt-25">
                        <p className="clr-white">
                          Already a partner?{" "}
                          <a href="https://partner.axoix.com/login" style={{ color: "var(--color-primary)" }}>Log in here</a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default BecomePartner;
